import { defineListener, errorView, type ModuleListener } from '@krp/client-runtime'

import { normalizeRid } from '../../../shared/model/scopeKeys'
import type { InventoryPortsFactory } from '../../inventory/model/ports'
import { priceTagScanRequested } from './commands'
import { priceTagAdded, priceTagScanSettled, priceTagScanStarted } from './priceTagsSlice'
import type { PriceTagScanState, PriceTagsState } from './types'

export interface PriceTagsStateAccess<S> {
  tags: (state: S) => PriceTagsState
  scan: (state: S) => Record<string, PriceTagScanState>
}

/** Listener подбора ценников: скан → позиция с ценой выбранного склада и временем цены. */
export function createPriceTagsListeners<S>(
  makePorts: InventoryPortsFactory,
  access: PriceTagsStateAccess<S>,
): ModuleListener<S>[] {
  return [
    defineListener<S, ReturnType<typeof priceTagScanRequested>>(priceTagScanRequested.match, async (action, api) => {
      const resourceId = normalizeRid(action.payload.resourceId)
      const barcode = action.payload.barcode.trim()
      const selection = access.tags(api.getModuleState()).byResource[resourceId]
      if (barcode === '' || selection === undefined || selection.warehouseRefKey === '') return
      if (access.scan(api.getModuleState())[resourceId]?.busy === true) return
      const warehouseRefKey = selection.warehouseRefKey
      const ports = makePorts(api.dispatch)
      api.dispatch(priceTagScanStarted({ resourceId }))
      const outcome = await ports.scan(resourceId, warehouseRefKey, barcode)
      // Выход пользователя или очистка базы во время скана: позицию не добавлять.
      if (access.scan(api.getModuleState())[resourceId]?.busy !== true) return
      if (!outcome.ok) {
        api.dispatch(priceTagScanSettled({ resourceId, error: errorView(outcome.error), notFound: null }))
        return
      }
      const found = outcome.value
      if (!found.found || found.productRefKey === null) {
        api.dispatch(
          priceTagScanSettled({
            resourceId,
            error: null,
            notFound: `Штрихкод ${barcode} не найден в 1С этой базы — ценник печатать не с чего.`,
          }),
        )
        return
      }
      api.dispatch(
        priceTagAdded({
          resourceId,
          warehouseRefKey,
          item: {
            productRefKey: found.productRefKey,
            name: found.name ?? '',
            barcode: found.barcode,
            price: found.prices.find((price) => price.warehouseRefKey === warehouseRefKey)?.price ?? null,
            copies: 1,
            pricedAt: ports.now(),
          },
        }),
      )
      api.dispatch(priceTagScanSettled({ resourceId, error: null, notFound: null }))
    }),
  ]
}
